/* ═══════════════════════════════════════════════════════════════
   REVIEW — the bar: "Change 3 of 12 · Previous · Next · Accept · Reject
   · Accept all remaining · Finish". Finish is the one primary.

   `mode: 'walk'` is the self-review before Publish (D3): Previous / Next
   only, and Finish reads "Continue".

   Keyboard (while the bar is up and no dialog is open): ← / → move,
   A / R accept / reject, Esc leaves Review.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Check, CheckCheck, ChevronLeft, ChevronRight, LogOut, Undo2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Hint } from '../components/ui/tooltip';
import { Icon } from '../components/ui/Icon';
import type { Decision } from '../../shared/sync';
import './review.css';

export interface ReviewBarProps {
  /** Which updates this bar walks: the master's, the project forma's, or a
   *  Revert to an older version (Tweaks 36 — Accept all · Reject all ·
   *  Apply revert · Cancel; nothing is written until Apply revert). */
  lane: 'master' | 'forma' | 'revert';
  /** The bar's title when the lane names itself ("Reverting to version 3 —
   *  accept the changes you want back"). */
  title?: string;
  /** Revert lane: answer every change "keep today's". */
  onRejectAll?: () => void;
  mode: 'review' | 'walk';
  /** Zero-based; -1 when there is nothing to walk. */
  index: number;
  total: number;
  /** The current change's decision, if any. */
  decision?: Decision | null;
  onPrev: () => void;
  onNext: () => void;
  onAccept?: () => void;
  onReject?: () => void;
  onAcceptAll?: () => void;
  canUndoAcceptAll?: boolean;
  onUndoAcceptAll?: () => void;
  /** Finish (review) / Continue (walk). */
  onFinish: () => void;
  onLeave: () => void;
  /** specs.publish — without it Accept / Reject / Finish are not offered. */
  canDecide: boolean;
  /** Undecided changes left. */
  remaining?: number;
  busy?: boolean;
  error?: string | null;
  /** A one-line reason shown in the bar (e.g. why editing is paused). */
  note?: string | null;
}

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable;
}

export function ReviewBar({
  lane,
  mode,
  index,
  total,
  decision,
  onPrev,
  onNext,
  onAccept,
  onReject,
  onAcceptAll,
  canUndoAcceptAll,
  onUndoAcceptAll,
  onFinish,
  onLeave,
  canDecide,
  remaining = 0,
  busy = false,
  error,
  note,
  title,
  onRejectAll,
}: ReviewBarProps) {
  const walk = mode === 'walk';
  const revert = lane === 'revert';
  const deciding = !walk && canDecide;
  const has = index >= 0 && total > 0;

  // Read the live handlers through a ref so the listener is bound once.
  const live = React.useRef({ onPrev, onNext, onAccept, onReject, onLeave, deciding, has, busy });
  live.current = { onPrev, onNext, onAccept, onReject, onLeave, deciding, has, busy };
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      const h = live.current;
      if (e.key === 'Escape') {
        e.preventDefault();
        h.onLeave();
        return;
      }
      if (!h.has || h.busy) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        h.onPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        h.onNext();
      } else if (h.deciding && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        h.onAccept?.();
      } else if (h.deciding && (e.key === 'r' || e.key === 'R')) {
        e.preventDefault();
        h.onReject?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const finishLabel = walk
    ? 'Continue'
    : revert
      ? busy
        ? 'Applying…'
        : 'Apply revert'
      : busy
        ? 'Finishing…'
        : 'Finish';
  const leaveLabel = revert ? 'Cancel' : 'Leave Review';

  return (
    <div className="sf-review-bar-host">
      <div className="sf-review-bar" role="toolbar" aria-label="Review">
        <span className="sf-review-bar-title">
          {title ?? (walk ? 'Reviewing your changes' : `Reviewing ${lane} updates`)}
        </span>
        <span className="sf-review-bar-count" aria-live="polite">
          {has ? `· ${index + 1} of ${total}` : '· No changes'}
        </span>

        <span className="sf-review-bar-group">
          <Hint text="Previous change (←)">
            <Button
              variant="outline"
              size="sm"
              onClick={onPrev}
              disabled={!has || index <= 0}
              aria-label="Previous change"
            >
              <Icon icon={ChevronLeft} aria-hidden="true" />
              <span className="sf-review-bar-label">Previous</span>
            </Button>
          </Hint>
          <Hint text="Next change (→)">
            <Button
              variant="outline"
              size="sm"
              onClick={onNext}
              disabled={!has || index >= total - 1}
              aria-label="Next change"
            >
              <span className="sf-review-bar-label">Next</span>
              <Icon icon={ChevronRight} aria-hidden="true" />
            </Button>
          </Hint>
        </span>

        {deciding && (
          <span className="sf-review-bar-group">
            <Hint text="Accept this change (A)">
              <Button
                variant="outline"
                size="sm"
                onClick={onAccept}
                disabled={!has || busy}
                aria-pressed={decision === 'accept'}
                aria-label="Accept"
              >
                <Icon icon={Check} aria-hidden="true" />
                <span className="sf-review-bar-label">Accept</span>
              </Button>
            </Hint>
            <Hint text="Reject this change (R)">
              <Button
                variant="outline"
                size="sm"
                onClick={onReject}
                disabled={!has || busy}
                aria-pressed={decision === 'reject'}
                aria-label="Reject"
              >
                <Icon icon={X} aria-hidden="true" />
                <span className="sf-review-bar-label">Reject</span>
              </Button>
            </Hint>
            {revert ? (
              <>
                <Hint text="Accept every change — take all the old words back">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onAcceptAll}
                    disabled={busy || total === 0}
                    aria-label="Accept all"
                  >
                    <Icon icon={CheckCheck} aria-hidden="true" />
                    <span className="sf-review-bar-label">Accept all</span>
                  </Button>
                </Hint>
                <Hint text="Reject every change — keep today's words">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRejectAll}
                    disabled={busy || total === 0}
                    aria-label="Reject all"
                  >
                    <Icon icon={X} aria-hidden="true" />
                    <span className="sf-review-bar-label">Reject all</span>
                  </Button>
                </Hint>
              </>
            ) : canUndoAcceptAll ? (
              <Hint text="Undo accept all">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onUndoAcceptAll}
                  disabled={busy}
                  aria-label="Undo accept all"
                >
                  <Icon icon={Undo2} aria-hidden="true" />
                  <span className="sf-review-bar-label">Undo accept all</span>
                </Button>
              </Hint>
            ) : (
              <Hint text="Accept all remaining">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onAcceptAll}
                  disabled={busy || remaining === 0}
                  aria-label="Accept all remaining"
                >
                  <Icon icon={CheckCheck} aria-hidden="true" />
                  <span className="sf-review-bar-label">Accept all remaining</span>
                </Button>
              </Hint>
            )}
          </span>
        )}

        {note && <span className="sf-review-bar-note">{note}</span>}
        <span className="sf-review-bar-spacer" />

        <Hint text={revert ? 'Cancel — nothing changes (Esc)' : 'Leave Review (Esc)'}>
          <Button
            variant="ghost"
            size="sm"
            onClick={onLeave}
            disabled={busy}
            aria-label={leaveLabel}
          >
            <Icon icon={LogOut} aria-hidden="true" />
            <span className="sf-review-bar-label">{leaveLabel}</span>
          </Button>
        </Hint>
        {(walk || canDecide) && (
          <Button size="sm" onClick={onFinish} disabled={busy}>
            {finishLabel}
          </Button>
        )}

        {error && (
          <p className="sf-review-bar-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
